// components/Students/AddStudentPage.js
import React, { useEffect, useState } from "react";
import StudentForm from "./StudentForm";
import apiClient from "../../utils/apiClient";
import { courseListFee } from "../../utils/enrolmentFee";
import { useNavigate } from "react-router-dom";
import { useHasStudentModule } from "../../contexts/PlanContext";
import { staffDocumentsApi, uploadProblem } from "./profile/documentsApi";

const initialForm = {
  name: "",
  email: "",
  mobile: "",
  dob: "",
  gender: "",
  address: "",
  course: "",
  batch: "",
  degree: "",
  degreeBranch: "",
  college: "",
  percentage: "",
  passout: "",
  experience: "",
  workHistory: [],
  totalFees: "",
  paidFees: "",
  pendingFees: "",
  placement: "Yes",
  refName: "",
  refNumber: "",
  comments: "",
};

/**
 * The documents queued on the form point at companies by the form's own
 * temporary ids; the saved student has the real ones, in the same order.
 */
const companyIdMap = (formRows = [], savedRows = []) => {
  const map = new Map();
  formRows.forEach((row, i) => {
    if (row._id && savedRows[i]?._id) map.set(String(row._id), String(savedRows[i]._id));
  });
  return map;
};

const AddStudentPage = () => {
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [loading, setLoading] = useState(false);
  const [courseOptions, setCourseOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [feeError, setFeeError] = useState(null);
  // The queued documents going up after the save: { done, total, percent, name }.
  const [uploading, setUploading] = useState(null);
  const navigate = useNavigate();
  const advancedProfile = useHasStudentModule()("advancedProfile");

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const { data } = await apiClient.get("/courses/list");
        // `fee` is the course's own fee, filled in when the course is picked.
        setCourseOptions((data.courses || []).map(c => ({ value: c._id, label: c.name, fee: courseListFee(c) })));
      } catch {
        setCourseOptions([]);
      }
    };
    fetchCourses();
  }, []);

  const fetchBatches = async (courseId) => {
    if (!courseId) return setBatchOptions([]);
    try {
      const { data } = await apiClient.get("/batches/list", { params: { course: courseId } });
      setBatchOptions((data.batches || []).map(b => ({ value: b._id, label: b.name })));
    } catch {
      setBatchOptions([]);
    }
  };

  /**
   * Upload the files queued on the form, now that the student exists, one at
   * a time with the progress shown in the form's action bar. Returns the failures.
   */
  const uploadQueued = async (student, formRows, pendingDocs) => {
    const api = staffDocumentsApi(student._id);
    const companies = companyIdMap(formRows, student.workHistory || []);
    const failures = [];
    const total = pendingDocs.length;
    for (let i = 0; i < total; i += 1) {
      const item = pendingDocs[i];
      const companyId = item.companyId ? companies.get(String(item.companyId)) || item.companyId : undefined;
      setUploading({ done: i, total, percent: 0, name: item.file.name });
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.upload({ ...item, companyId }, {
          onProgress: (percent) => setUploading((u) => (u ? { ...u, percent } : u)),
        });
      } catch (err) {
        failures.push(`${item.file.name}: ${uploadProblem(err)}`);
      }
    }
    setUploading(null);
    return failures;
  };

  const handleAdd = async (formData, additionalCourses = [], pendingDocs = []) => {
    setLoading(true);
    setFeeError(null);
    try {
      const payload = { ...formData };
      if (!payload.course) payload.course = null;
      if (!payload.batch) payload.batch = null;
      // Include additional courses in payload
      if (additionalCourses.length > 0) {
        payload.additionalCourses = additionalCourses.filter(ac => ac.courseId);
      }
      const { data } = await apiClient.post("/students/add", payload);
      const student = data?.student;

      if (pendingDocs.length && student?._id) {
        const failures = await uploadQueued(student, formData.workHistory || [], pendingDocs);
        if (failures.length) {
          setToast({ show: true, message: `Student added. ${failures.length} document${failures.length === 1 ? "" : "s"} could not be uploaded — ${failures[0]}`, type: "warning" });
        } else {
          setToast({ show: true, message: `Student added with ${pendingDocs.length} document${pendingDocs.length === 1 ? "" : "s"}.`, type: "success" });
        }
        setTimeout(() => navigate(`/students/${student._id}?tab=documents`), 1500);
      } else {
        setToast({ show: true, message: "Student added successfully!", type: "success" });
        setTimeout(() => navigate("/students"), 1200);
      }
    } catch (error) {
      console.error("Error adding student:", error);
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to add student.";
      setToast({ show: true, message: apiMessage, type: "error" });
      // A fee the server refused names its course; show it there too.
      const courseId = error?.response?.data?.courseId;
      if (courseId) setFeeError({ courseId, message: apiMessage });
    }
    setLoading(false);
  };

  return (
    <StudentForm
      initialData={initialForm}
      courseOptions={courseOptions}
      batchOptions={batchOptions}
      loading={loading}
      toast={toast}
      setToast={setToast}
      mode="add"
      onSubmit={handleAdd}
      onChangeCourse={fetchBatches}
      feeError={feeError}
      advancedProfile={advancedProfile}
      uploading={uploading}
    />
  );
};

export default AddStudentPage;
